import { createSignal, createEffect, onCleanup, Show, untrack } from "solid-js";
import { View, Text } from "@lightningtv/solid";
import { useLocation, useNavigate } from "@solidjs/router";
import {
  isBuffering,
  playListPassword,
  playListUrl,
  playListUsername,
  setIsBuffering,
  setIsPlaying,
  setLastStream,
  setLiveTvStreamID,
  setPreviewToNotShow,
  STREAM_EXTENSION_STORAGE_KEY,
} from "../../../state";
import axios from "axios";
import OSD from "./Osd";
import videoPlayer from "../../video";
import LoadingIndicator from "../loading/loading";
import { Buffer } from "buffer";
import ConfirmPin from "../../components/ConfirmPin";
import { dnsResolver } from "../../api/providers/dnsResolver";
import { logger } from "../../utils/logger";
import { useNetworkMonitor } from "../../hooks/useNetworkMonitor";
import NetworkOverlay from "../../components/NetworkOverlay";
import { getProxyHeaders } from "../../api/proxyHeaders";

// ── Helpers ──────────────────────────────────────────────────────────────────

async function resolveUrl(originalUrl: string): Promise<string> {
  let finalUrl = originalUrl;

  try {
    const dnsResult = await dnsResolver.transformUrl(originalUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalUrl = dnsResult.url;
    } else if (typeof dnsResult === "string" && dnsResult !== originalUrl) {
      finalUrl = dnsResult;
    }
  } catch (_) {
    // DNS failed — use original
  }

  if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
    const encoded = Buffer.from(finalUrl, "utf-8").toString("base64");
    finalUrl = localStorage.getItem("proxy_url")! + encoded;
  }

  return finalUrl;
}

async function resolveApiUrl(
  originalUrl: string
): Promise<{ url: string; headers: Record<string, string> }> {
  let finalUrl = originalUrl;
  const headers: Record<string, string> = {};

  try {
    const dnsResult = await dnsResolver.transformUrl(originalUrl);
    if (typeof dnsResult === "object" && dnsResult.url) {
      finalUrl = dnsResult.url;
      headers["Host"] = dnsResult.originalHost;
    } else if (typeof dnsResult === "string" && dnsResult !== originalUrl) {
      finalUrl = dnsResult;
    }
  } catch (_) {
    // DNS failed — use original
  }

  if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
    const encoded = Buffer.from(finalUrl, "utf-8").toString("base64");
    finalUrl = localStorage.getItem("proxy_url")! + encoded;
    Object.assign(headers, getProxyHeaders());
  }

  return { url: finalUrl, headers };
}

// ── Component ────────────────────────────────────────────────────────────────

const LiveTvPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [data, setData] = createSignal<any[]>([]);
  const [liveChannel, setLiveChannel] = createSignal<any>({});
  const [currentIndex, setCurrentIndex] = createSignal(0);
  const [currentStream, setCurrentStream] = createSignal<any>();
  const [currentStreamUrl, setCurrentStreamUrl] = createSignal<string>("");
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [showOSD, setShowOSD] = createSignal(true);
  const [osdQueue, setOsdQueue] = createSignal<any[]>([]);
  const [isFromSearch, setIsFromSearch] = createSignal<boolean | null>(null);
  const [listOfFav, setListOfFav] = createSignal<any>({});
  const [isFavChannel, setIsFavChannel] = createSignal(false);
  const [isChannelLocked, setIsChannelLocked] = createSignal(false);
  const [showPinEntry, setShowPinEntry] = createSignal(false);

  // ── Network state ──────────────────────────────────────────────────────────

  const network = useNetworkMonitor();
  const [networkLost, setNetworkLost] = createSignal(false);
  const [showNetworkOverlay, setShowNetworkOverlay] = createSignal(false);
  const [resumeAttempt, setResumeAttempt] = createSignal(0);
  const MAX_RESUME_ATTEMPTS = 3;

  let savedPlayerState: any = null;
  let resumeHandle: any = null;
  let attemptPoller: ReturnType<typeof setInterval> | null = null;

  // Go offline → pause player and show overlay
  createEffect(() => {
    if (!network.isConnected() && isVideoPlaying()) {
      setNetworkLost(true);
      setShowNetworkOverlay(true);
      // Do NOT touch showOSD here — let it hide on its own timeout
      setResumeAttempt(0);
      savedPlayerState = videoPlayer.networkPause?.() ?? null;
    }
  });

  // Come back online → trigger resume
  createEffect(() => {
    if (network.isInternetReachable() && networkLost()) {
      handleNetworkResume();
    }
  });

  function handleNetworkResume() {
    if (!savedPlayerState) return;
    if (resumeHandle) resumeHandle.cancel();

    resumeHandle = videoPlayer.networkResume(
      savedPlayerState,
      // onResumeSuccess
      () => {
        setNetworkLost(false);
        setShowNetworkOverlay(false);
        setResumeAttempt(0);
        setIsVideoPlaying(true);
        // Restore OSD so the user sees the channel info again
        setShowOSD(true);
        resetOSDTimeout();
        savedPlayerState = null;
        resumeHandle = null;
        if (attemptPoller) { clearInterval(attemptPoller); attemptPoller = null; }
      },
      // onResumeFailed
      () => {
        setResumeAttempt(MAX_RESUME_ATTEMPTS);
        resumeHandle = null;
        if (attemptPoller) { clearInterval(attemptPoller); attemptPoller = null; }
      }
    );

    // Poll resumeHandle.attempt so the overlay can show progress
    if (attemptPoller) clearInterval(attemptPoller);
    attemptPoller = setInterval(() => {
      if (resumeHandle) {
        setResumeAttempt(resumeHandle.attempt);
      } else {
        clearInterval(attemptPoller!);
        attemptPoller = null;
      }
    }, 500);
  }

  function handleOverlayRetry() {
    // retryNow() starts an async reachability check.
    // The createEffect watching isInternetReachable() will call
    // handleNetworkResume() automatically — don't call it here.
    network.retryNow();
  }

  function handleOverlayGoBack() {
    if (resumeHandle) { resumeHandle.cancel(); resumeHandle = null; }
    if (attemptPoller) { clearInterval(attemptPoller); attemptPoller = null; }
    onCustomEscape();
  }

  // ── Parse URL params ───────────────────────────────────────────────────────

  let timeoutId: ReturnType<typeof setTimeout>;

  createEffect(() => {
    const params = new URLSearchParams(location.search);
    setIsFromSearch(params.get("isTrue") === "true");
    setIsChannelLocked(params.get("isLocked") === "true");

    const num = parseInt(params.get("num") || "0", 10);
    const channelData = data();
    if (!channelData || channelData.length === 0) return;

    const result: any = channelData.find((stream: any) => stream.stream_id === num);
    if (result) {
      setCurrentStream(result.stream_id);
      setCurrentIndex(result.num - 1);
    }
  });

  // ── Fetch live streams ─────────────────────────────────────────────────────

  createEffect(async () => {
    const username = untrack(playListUsername);
    const password = untrack(playListPassword);
    const playList = untrack(playListUrl);

    const originalApiUrl = `${playList}/player_api.php?username=${username}&password=${password}&action=get_live_streams`;
    const { url: finalApiUrl, headers } = await resolveApiUrl(originalApiUrl);

    axios
      .get(finalApiUrl, { headers })
      .then((response) => setData(response.data))
      .catch((error) => logger.error("LiveTV API call failed:", error));
  });

  // ── Update current channel on index change ─────────────────────────────────

  createEffect(() => {
    const index = currentIndex();
    const channelData = data();
    if (channelData.length > 0 && index >= 0 && index < channelData.length) {
      setLiveChannel(channelData[index]);
    }
  });

  // ── Channel navigation ─────────────────────────────────────────────────────

  const incrementIndex = async () => {
    const newIndex = currentIndex() + 1;
    const channelData = data();
    const nextChannel = channelData[newIndex];
    if (newIndex < channelData.length && nextChannel) {
      setCurrentIndex(newIndex);
      setLiveChannel(nextChannel);
      setOsdQueue((prev) => [...prev, nextChannel.num]);
      setShowOSD(true);
      await loadNewStream(nextChannel.stream_id);
      setIsBuffering(true);
      resetOSDTimeout();
    }
  };

  const decrementIndex = async () => {
    const newIndex = currentIndex() - 1;
    const channelData = data();
    const prevChannel = channelData[newIndex];
    if (newIndex >= 0 && prevChannel) {
      setCurrentIndex(newIndex);
      setLiveChannel(prevChannel);
      setOsdQueue((prev) => [...prev, prevChannel.num]);
      setShowOSD(true);
      await loadNewStream(prevChannel.stream_id);
      setIsBuffering(true);
      resetOSDTimeout();
    }
  };

  // ── Stream loading ─────────────────────────────────────────────────────────

  const loadNewStream = async (streamId: any) => {
    if (videoPlayer) await videoPlayer.destroy();

    setCurrentStream(streamId);
    setLastStream(streamId);

    const ext = localStorage.getItem(STREAM_EXTENSION_STORAGE_KEY) ?? ".ts";
    const originalUrl = `${playListUrl()}/live/${playListUsername()}/${playListPassword()}/${streamId}${ext}`;
    const finalUrl = await resolveUrl(originalUrl);
    // Keep a local copy of the resolved URL for network resume
    setCurrentStreamUrl(finalUrl);

    await playCurrentStream(finalUrl);
  };

  const playCurrentStream = async (streamUrl: string) => {
    try {
      videoPlayer.destroy();
      videoPlayer
        .playVideo(streamUrl, "background")
        .catch((error: any) => logger.error("Error playing video:", error));
      setIsVideoPlaying(true);
    } catch (error) {
      logger.error("Error playing video:", error);
      setIsVideoPlaying(false);
    }
  };

  const onEnterTrailer = async (context: string) => {
    const streamOfID = currentStream();
    const ext = localStorage.getItem(STREAM_EXTENSION_STORAGE_KEY) ?? ".ts";
    const originalUrl = `${playListUrl()}/live/${playListUsername()}/${playListPassword()}/${streamOfID}${ext}`;
    const finalUrl = await resolveUrl(originalUrl);

    // Keep local copy for network resume
    setCurrentStreamUrl(finalUrl);

    videoPlayer
      .playVideo(finalUrl, context)
      .then(() => {
        setIsBuffering(false);
        setIsVideoPlaying(true);
      })
      .catch((error: any) => {
        logger.error("Error playing video:", error);
        setIsVideoPlaying(false);
      });
  };

  // ── Escape ─────────────────────────────────────────────────────────────────

  function onCustomEscape() {
    setLiveTvStreamID(null);
    setIsBuffering(false);
    setPreviewToNotShow(true);
    videoPlayer.setPreviewScreen();
    navigate(-1);
  }

  // ── PIN confirmation ───────────────────────────────────────────────────────

  const handlePinConfirmed = () => {
    setShowPinEntry(false);
    setIsChannelLocked(false);
    onEnterTrailer("background");
  };

  createEffect(() => {
    const current = currentStream();
    const fromSearch = isFromSearch();
    const locked = isChannelLocked();

    if (!current || fromSearch !== true) return;

    if (locked) {
      setShowPinEntry(true);
    } else {
      onEnterTrailer("background");
    }
  });

  // ── OSD timeout ────────────────────────────────────────────────────────────

  createEffect(() => {
    // Don't fight with the network overlay — leave OSD state alone while offline
    if (showNetworkOverlay()) return;
    setShowOSD(true);
    resetOSDTimeout();
  });

  const resetOSDTimeout = () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      setShowOSD(false);
      setOsdQueue([]);
    }, 8000);
  };

  createEffect(() => {
    if (liveChannel()?.stream_id && data().length > 0) {
      setShowOSD(true);
      resetOSDTimeout();
    }
  });

  // ── Cleanup ────────────────────────────────────────────────────────────────

  onCleanup(() => {
    clearTimeout(timeoutId);
    if (resumeHandle) { resumeHandle.cancel(); resumeHandle = null; }
    if (attemptPoller) { clearInterval(attemptPoller); attemptPoller = null; }
  });

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <View
      onDown={() => { if (!showPinEntry()) decrementIndex(); }}
      onUp={() => { if (!showPinEntry()) incrementIndex(); }}
      onEscape={() => {
        if (showPinEntry()) setShowPinEntry(false);
        onCustomEscape();
      }}
      autofocus
      onEnter={() => { }}
    >
      {/* PIN Entry Screen */}
      <Show when={showPinEntry()}>
        <View color={"#000"} width={1920} height={1080}>
          <View x={760} y={150}>
            <Text fontSize={48} color={"#ffffff"} textAlign={"center"} x={-20}>
              Channel is Locked
            </Text>
            <Text fontSize={32} color={"#cccccc"} textAlign={"center"} y={60} x={-60}>
              Please enter your PIN to continue
            </Text>
          </View>

          <ConfirmPin
            onPinSet={handlePinConfirmed}
            showBackground={false}
            title={" "}
            x={600}
            y={120}
          />

          <Show when={liveChannel()?.stream_id && showOSD()}>
            <OSD isLocked={true} channel={liveChannel()} />
          </Show>
        </View>
      </Show>

      {/* Regular OSD */}
      <Show when={liveChannel()?.stream_id && showOSD() && !showPinEntry()}>
        <OSD isLocked={false} channel={liveChannel()} />
      </Show>

      {/* Loading indicator */}
      <Show when={isBuffering() && !showPinEntry()}>
        <View x={850} y={400}>
          <LoadingIndicator />
        </View>
      </Show>

      {/* Network loss overlay */}
      <NetworkOverlay
        visible={showNetworkOverlay()}
        isReconnecting={
          network.isReconnecting() ||
          (resumeAttempt() > 0 && resumeAttempt() < MAX_RESUME_ATTEMPTS)
        }
        connectionType={network.connectionType()}
        attemptNumber={resumeAttempt()}
        maxAttempts={MAX_RESUME_ATTEMPTS}
        onRetry={handleOverlayRetry}
        onGoBack={handleOverlayGoBack}
      />
    </View>
  );
};

export default LiveTvPage;